'use client';

import { Lock, TickWhite } from 'assets/icons';
import { Container, useCartStore, useShallow } from 'components/shared';
import CustomLink from 'components/shared/CustomLink';
import { ReactNode } from 'react';
import { standaloneUhxShopifyMap } from 'content/products/uhx';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { useForwardSearchParamsShopify } from 'hooks/navigation/useForwardSearchParamsShopify';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getAssetUrl } from 'lib/config/assetUrls';
import { Currency } from 'utils/Currency';
import styles from './BuySection.module.css';

type BenefitState = 'included' | 'preincluded' | 'locked';

const ProductCard = ({
  validity,
  price,
  currency,
  saving,
  billedAt,
  // productId,
  highlighted,
  benefits,
  checkoutUrl,
}: // benefitsResponsive,
{
  className?: string;
  validity?: string;
  price?: number;
  currency?: Currency;
  saving?: number;
  billedAt?: string;
  // productId: string;
  highlighted?: boolean;
  benefits?: Array<{
    image?: string;
    title: string | ReactNode;
    state?: BenefitState;
  }>;
  checkoutUrl?: string;
  // benefitsResponsive?: Array<{
  //   image?: string;
  //   title: string | ReactNode;
  //   state?: BenefitState;
  // }>;
}) => {
  const CartClient = useCartStore(
    useShallow((s) => ({ taxString: s.taxString, store: s.store })),
  );
  const urlFormat = useForwardSearchParamsShopify();
  const formatPriceWithComma = useFormatPrice();

  return (
    <div
      className={`${styles.productCard} ${highlighted ? 'highlighted' : ''}`}
    >
      {/*  */}
      <div className={styles['card-header']}>
        <div className={styles.row}>
          <p className={styles.caption}>{validity} YEAR MEMBERSHIP</p>
          {saving ? (
            <span className={styles['save-tag']}>Save {saving}%</span>
          ) : null}
        </div>

        <div className={styles.row}>
          {currency && price ? (
            <p className={`${styles.price} price`}>
              {CurrencySymbol[currency]}
              {formatPriceWithComma(Number(price))}
              <i> / year</i>
            </p>
          ) : null}
          <p className={styles['billed-at']}>
            Billed {currency ? CurrencySymbol[currency] : null}
            {billedAt} one time
            <br />
            {CartClient.taxString}
          </p>
        </div>
      </div>
      <hr />
        {/* <div className="benefits-container sm">
          {benefitsResponsive?.map((benefit, index) => (
            <div key={index} className="benefit">
              <img
                src={benefit.image}
                alt={benefit.title as string}
                width={48}
                height={48}
              />
              <p>{benefit.title}</p>
              {benefit.state === 'included' ? (
                <TickWhite className="state-icon" fill="#00FF66" />
              ) : null}
              {benefit.state === 'preincluded' ? (
                <TickWhite
                  className="state-icon"
                  fill="rgba(255,255,255,0.9)"
                />
              ) : null}
              {benefit.state === 'locked' ? (
                <Lock className="state-icon" fill="rgba(255,255,255,0.9)" />
              ) : null}
            </div>
          ))}
        </div> */}
        <div className={styles['benefits-container']}>
          {benefits?.map((benefit, index) => (
            <div key={index} className={styles.benefit}>
              <img
                src={benefit.image}
                alt={benefit.title as string}
                width={48}
                height={48}
              />
              <p>{benefit.title}</p>
              {benefit.state === 'included' ? (
                <TickWhite className={styles['state-icon']} fill="#00FF66" />
              ) : null}
              {benefit.state === 'preincluded' ? (
                <TickWhite
                  className={styles['state-icon']}
                  fill="rgba(255,255,255,0.9)"
                />
              ) : null}
              {benefit.state === 'locked' ? (
                <Lock
                  className={styles['state-icon']}
                  fill="rgba(255,255,255,0.9)"
                />
              ) : null}
            </div>
          ))}
        </div>
        {/* {CartClient.store !== ShopifyStore.US ? (
          <div className="disclaimer-in-card">
            <p>*Only applicable for Ring AIR</p>
          </div>
        ) : null} */}
        <div className={styles['cta-container']}>
          <CustomLink
            href={urlFormat(
              checkoutUrl as string,
              undefined,
              CartClient.store ?? undefined,
            )}
            className={`${styles.cta} ${highlighted ? styles.primary : ''}`}
          >
            Buy now
          </CustomLink>
        </div>
      </div>
    );
};

export const BuySection = ({
  className,
  country,
}: {
  className?: string;
  country?: string;
}) => {
  const CartClient = useCartStore(useShallow((s) => ({ store: s.store })));
    const { store, currency: cartCurrency } = useCartStore(
      useShallow((s) => ({ store: s.store, currency: s.currency })),
    );

    const UhxProducts =
      standaloneUhxShopifyMap[CartClient.store ?? ShopifyStore.ROW];

    const savings = Math.floor(
      ((UhxProducts.Year1.price - UhxProducts.Year2.price / 2) /
        UhxProducts.Year1.price) *
        100,
    );

    const M1_ElementCopy = (() => {
      switch (CartClient.store) {
        case ShopifyStore.US:
          return 'Enjoy 15% off on M1 Live all plans';
        case ShopifyStore.AE:
          return 'Enjoy 12% off on M1 Live all plans';
        case ShopifyStore.IN:
          return 'Enjoy 15% off on all M1 plans';
        case ShopifyStore.EU:
          return 'Enjoy 15% off on all M1 plans';
        case ShopifyStore.GB:
          return 'Enjoy 15% off on all M1 plans';
        case ShopifyStore.AU:
          return 'Enjoy 15% off on all M1 plans';
        case ShopifyStore.CA:
          return 'Enjoy 15% off on all M1 Live plans';
        case ShopifyStore.ROW:
          return 'Enjoy 15% off on all M1 Live plans';
        default:
          return 'Enjoy 15% off on all M1 Live plans';
      }
    })();

    const getUhxBenefits = (
      validityInYears: number,
    ): Array<{
      image?: string;
      title: string | ReactNode;
      state?: BenefitState;
    }> => {
      const op: Array<{
        image?: string;
        title: string | ReactNode;
        state?: BenefitState;
      }> = [
        {
          title: 'Accidental damage protection',
          state: validityInYears === 1 ? 'included' : 'preincluded',
          image: getAssetUrl('/web_v2/ultrahumanX/icons/accidental.svg'),
        },
        {
          title: 'Free, express replacements',
          state: validityInYears === 1 ? 'included' : 'preincluded',
          image: getAssetUrl('/web_v2/ultrahumanX/icons/replacements.svg'),
        },
        {
          title: 'Priority support',
          state: validityInYears === 1 ? 'included' : 'preincluded',
          image: getAssetUrl('/web_v2/ultrahumanX/icons/support.svg'),
        },
        {
          title: M1_ElementCopy,
          state: validityInYears === 1 ? 'included' : 'preincluded',
          image: getAssetUrl('/web_v2/ultrahumanX/icons/m1.svg'),
        },
      ];

      op.push(
        {
          title: 'Theft and loss protection \u00B9\u02D2\u00B2',
          state: validityInYears === 2 ? 'included' : 'locked',
          image: getAssetUrl('/web_v2/ultrahumanX/icons/loss-coverage.svg'),
        },
        {
          title: 'Weight loss coverage \u00B9\u02D2\u00B2',
          state: validityInYears === 2 ? 'included' : 'locked',
          image: getAssetUrl(
            '/web_v2/ultrahumanX/icons/weightloss-coverage.svg',
          ),
        },
        {
          title: 'Pregnancy Fit Coverage \u00B9\u02D2\u00B2',
          state: validityInYears === 2 ? 'included' : 'locked',
          image: getAssetUrl('/web_v2/ultrahumanX/icons/pregnancy.svg'),
        },
      );

      return op;
    };

    const disclaimer =
      country === 'US'
        ? "UltrahumanX must be activated within 30 days of your device's original purchase date. UltrahumanX membership covers all your Ultrahuman devices for the full duration of your selected plan. This applies to M1 CGM, Home Monitor, Ring PRO, and Ring AIR devices (purchased before Oct 21, 2025)."
        : "UltrahumanX must be activated within 30 days of your device's original purchase date. UltrahumanX membership covers all your Ultrahuman devices for the full duration of your selected plan. This applies to Ring AIR, Home Monitor, and M1.";

    const numberedDisclaimers = [
      store === ShopifyStore.US
        ? '\u00B9 Applicable for Ring PRO and Ring AIR devices (purchased before Oct 21, 2025)'
        : '\u00B9 Applicable for Ring PRO and Ring AIR',
      '\u00B2 A single replacement can be used for any one of the following reasons: loss or theft, weight-loss related size changes, or pregnancy-related fit changes.',
    ];

    return (
      <section className={`${styles.root}${className ? ` ${className}` : ''}`}>
        <Container>
          {[UhxProducts.Year1, UhxProducts.Year2].map((product) => (
            <ProductCard
              key={product.variantId}
              validity={`${product.validityInYears}`}
              // productId={product.variantId}
              checkoutUrl={product.checkouturl}
              price={product.pricePerYear}
              billedAt={product.price.toString()}
              currency={cartCurrency ?? Currency.usd}
              highlighted={product.validityInYears === 2}
              saving={product.validityInYears === 2 ? savings : undefined}
              benefits={getUhxBenefits(product.validityInYears)}
            />
          ))}
        </Container>
        <Container>
          <div className={styles.disclaimer}>
            <b>Disclaimer:</b>
            <br />
            <br />
            {disclaimer}
            {numberedDisclaimers.length > 0 && (
              <div className={styles['numbered-disclaimers']}>
                {numberedDisclaimers.map((d, i) => (
                  <p key={i}>{d}</p>
                ))}
              </div>
            )}
          </div>
        </Container>
      </section>
    );
};
